import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { InputBaseModel } from '../../models';
import { useAppStore } from '../../stores';
import './CompareContractButton.less';

interface Props {
    businessIdInput: InputBaseModel;
    layerId?: string;
}

const CompareContractButton: React.FC<Props> = ({ businessIdInput, layerId }) => {
    const { contractComparisonStore, baseCompanySegregationStore } = useAppStore();
    const selectedBaseCompanyId = baseCompanySegregationStore.state.selectedBaseCompanyId;

    const contractComparison = React.useMemo(
        () => contractComparisonStore.getOrCreateContractComparison(businessIdInput, { layerId }),
        [contractComparisonStore, businessIdInput, layerId, selectedBaseCompanyId]
    );

    React.useEffect(() => {
        contractComparison.reset();
    }, [contractComparison, contractComparison.businessId]);

    const compareAction = contractComparison.compareAction;

    const handleClick = () => {
        if (!compareAction.isClickable) {
            return;
        }

        contractComparisonStore.openOrLoadContractComparison(contractComparison);
    };

    const button = (
        <Button
            type="link"
            className={[
                'compare-contract-button',
                `compare-contract-button--${compareAction.state}`,
                compareAction.isClickable ? 'compare-contract-button--active' : 'compare-contract-button--disabled'
            ].join(' ')}
            onClick={handleClick}
            disabled={!compareAction.isClickable}
            data-testid="compare-contract-action"
            data-state={compareAction.state}
            data-icon={compareAction.icon ?? 'none'}
        >
            {compareAction.icon === 'loading' && (
                <LoadingOutlined
                    className="compare-contract-button__icon"
                    data-testid="compare-contract-action-icon"
                    spin
                />
            )}

            {compareAction.icon === 'check' && (
                <CheckOutlined
                    className="compare-contract-button__icon"
                    data-testid="compare-contract-action-icon"
                />
            )}

            <span className="compare-contract-button__label">{compareAction.label}</span>
        </Button>
    );

    if (!compareAction.tooltipTitle) {
        return button;
    }

    return (
        <Tooltip title={compareAction.tooltipTitle}>
            <span
                className="compare-contract-button__tooltip-target"
                data-testid="compare-contract-action-tooltip-target"
            >
                {button}
            </span>
        </Tooltip>
    );
};

export default observer(CompareContractButton);
